/* ============================================================
   SAM-650 — "Closures & special days" section of the business page Settings tab.
   Business-wide closures (a date range, or every year MM-DD) and special days (a date with
   its own opening windows), stored in `business.settings.closures` / `.special_days` and saved
   with the page's existing business update (PATCH /crud/business/:id), read-modify-write so
   no other `settings` key is lost. Pure logic lives in businessCalendar.ts.
   New Tailwind component (SAM-175 freeze); the recurring day picker and date conversion are
   the SAM-626 season editor's.
   ============================================================ */
import React, { useEffect, useMemo, useState } from 'react';
import { CalendarOff, CalendarClock, Info, Plus, Repeat, X } from 'lucide-react';
import { useBusinessById, useUpdateBusiness, type Business } from '../../hooks/useBusinessCrud';
import { MonthDayPicker, recurringHints } from '../../components/ServiceSeasonsEditor';
import { Checkbox } from '../../components/ui/Checkbox';
import { Notice } from '../../components/ui/Notice';
import { SAVE_FAILED_ROWS_TEXT, SAVE_FAILED_TEXT, useHeaderSave } from './headerSave';
import { Button } from '../../components/ui/Button';
import { DateField, TimeField, fieldBox } from '../../components/ui/DateTimeField';
import { describeMonthDay } from '../../utils/pricingSeasons';
import {
  describeCalendarEvent, emptyCalendarErrors, mapCalendarApiError, nextCalendarEvent, mergeCalendarIntoSettings, nextCalendarId, parseCalendar,
  readCalendarApiError, serializeCalendar, setClosureEveryYear, setSpecialDayEveryYear,
  specialDaysShadowedByClosures, validateCalendar,
  type BusinessClosureForm, type CalendarErrors, type CalendarForms, type SpecialDayForm,
} from './businessCalendar';

interface Props {
  business: Business;
}

const danger = 'var(--danger, #c0392b)';
const cardStyle = (bad: boolean): React.CSSProperties => ({
  border: `1px solid ${bad ? danger : 'var(--line)'}`,
  background: 'var(--surface, #fff)',
});

const ErrorLines: React.FC<{ messages?: string[] }> = ({ messages }) => (
  <>
    {(messages || []).map((m) => (
      <p key={m} role="alert" className="text-xs mt-2 mb-0" style={{ color: danger }}>{m}</p>
    ))}
  </>
);

const DateInput: React.FC<{ label: string; ariaLabel: string; value: string; onChange: (v: string) => void; invalid?: boolean }> = ({
  label, ariaLabel, value, onChange, invalid,
}) => <DateField label={label} ariaLabel={ariaLabel} value={value} onChange={onChange} invalid={invalid} />;

const textBox = `${fieldBox} px-2.5 border-[var(--line)]`;

const ReasonInputs: React.FC<{ prefix: string; en: string; es: string; onChange: (lang: 'en' | 'es', v: string) => void }> = ({
  prefix, en, es, onChange,
}) => (
  <>
    <label className="text-xs flex flex-col gap-1 min-w-0">
      <span style={{ color: 'var(--ink-500)' }}>Reason (EN, optional)</span>
      <input aria-label={`${prefix} reason EN`} value={en} placeholder="Christmas" className={textBox} onChange={(e) => onChange('en', e.target.value)} />
    </label>
    <label className="text-xs flex flex-col gap-1 min-w-0">
      <span style={{ color: 'var(--ink-500)' }}>Reason (ES, optional)</span>
      <input aria-label={`${prefix} reason ES`} value={es} placeholder="Navidad" className={textBox} onChange={(e) => onChange('es', e.target.value)} />
    </label>
  </>
);

const EveryYearToggle: React.FC<{ label: string; checked: boolean; onChange: (on: boolean) => void; hints: string[] }> = ({
  label, checked, onChange, hints,
}) => (
  <div className="mt-3 flex flex-col gap-1.5">
    <Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)} aria-label={label} style={{ fontSize: 12, color: 'var(--ink-700)' }}>
      <Repeat size={12} /> <span className="font-medium">Every year</span>
      <span style={{ color: 'var(--ink-400)' }}>&nbsp;— repeats on these days each year</span>
    </Checkbox>
    {hints.map((hint) => (
      <p key={hint} className="text-xs m-0 flex items-center gap-1.5" style={{ color: 'var(--ink-500)' }}>
        <Info size={12} /> {hint}
      </p>
    ))}
  </div>
);

/** `<input type="time">` cannot hold 24:00: a close of 00:00 is stored as "24:00" (end of day). */
const closeToInput = (v: string) => (v === '24:00' ? '00:00' : v);
const closeFromInput = (v: string) => (v === '00:00' ? '24:00' : v);

const selectBox = `${fieldBox} px-2 border-[var(--line)] w-auto`;

const snapshot = (forms: CalendarForms) => JSON.stringify(serializeCalendar(forms));

export const BusinessCalendarEditor: React.FC<Props> = ({ business }) => {
  const updateBusiness = useUpdateBusiness();
  // Same query the page reads; refetched right before saving so the merge starts from the
  // latest settings (another tab or the Advanced section may have changed them).
  const { refetch } = useBusinessById(business.id);

  const initial = useMemo(() => parseCalendar((business as { settings?: unknown }).settings), [business]);
  const [forms, setForms] = useState<CalendarForms>(initial);
  const [baseline, setBaseline] = useState(() => snapshot(initial));
  const [attempted, setAttempted] = useState(false);
  const [apiErrors, setApiErrors] = useState<CalendarErrors | null>(null);
  const [saving, setSaving] = useState(false);
  // SAM-1167: a failed save shows in the page header with «Try again»; the form keeps its changes.
  const [saveError, setSaveError] = useState<string | null>(null);

  const dirty = snapshot(forms) !== baseline;

  // A refreshed business (after our save, or someone else's) resets the editor unless the
  // user is in the middle of an edit.
  useEffect(() => {
    if (dirty) return;
    setForms(initial);
    setBaseline(snapshot(initial));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  const liveErrors = useMemo(() => validateCalendar(forms), [forms]);
  const errors: CalendarErrors = apiErrors ?? (attempted ? liveErrors : emptyCalendarErrors());
  const shadowed = useMemo(() => specialDaysShadowedByClosures(forms), [forms]);
  const upcoming = useMemo(() => nextCalendarEvent(forms), [forms]);

  const edit = (next: (f: CalendarForms) => CalendarForms) => {
    setApiErrors(null);
    setSaveError(null);
    setForms(next);
  };
  const patchClosure = (i: number, patch: Partial<BusinessClosureForm> | ((c: BusinessClosureForm) => BusinessClosureForm)) =>
    edit((f) => ({ ...f, closures: f.closures.map((c, j) => (j === i ? (typeof patch === 'function' ? patch(c) : { ...c, ...patch }) : c)) }));
  const patchSpecial = (i: number, patch: Partial<SpecialDayForm> | ((d: SpecialDayForm) => SpecialDayForm)) =>
    edit((f) => ({ ...f, specialDays: f.specialDays.map((d, j) => (j === i ? (typeof patch === 'function' ? patch(d) : { ...d, ...patch }) : d)) }));

  const addClosure = () => edit((f) => ({
    ...f,
    closures: [...f.closures, { id: nextCalendarId(f.closures, 'closure'), start_date: '', end_date: '', reason_en: '', reason_es: '' }],
  }));
  const addSpecial = () => edit((f) => ({
    ...f,
    specialDays: [...f.specialDays, {
      id: nextCalendarId(f.specialDays, 'special-day'), date: '', windows: [{ open: '09:00', close: '14:00' }], reason_en: '', reason_es: '',
    }],
  }));

  const handleCancel = () => {
    setForms(initial);
    setBaseline(snapshot(initial));
    setAttempted(false);
    setApiErrors(null);
    setSaveError(null);
  };

  const handleSave = async () => {
    setAttempted(true);
    setApiErrors(null);
    setSaveError(null);
    if (validateCalendar(forms).hasErrors) return;
    setSaving(true);
    try {
      const fresh = await refetch();
      if (fresh.isError || !fresh.data?.[0]) {
        setApiErrors({ ...emptyCalendarErrors(), general: 'Could not load the latest business settings. Try again.', hasErrors: true });
        setSaveError(SAVE_FAILED_TEXT);
        return;
      }
      const settings = mergeCalendarIntoSettings((fresh.data[0] as { settings?: unknown }).settings, serializeCalendar(forms));
      await updateBusiness.mutateAsync({ id: business.id, data: { settings } as Partial<Business> });
      setBaseline(snapshot(forms));
      setAttempted(false);
    } catch (err) {
      const api = readCalendarApiError(err);
      if (api) {
        const mapped = mapCalendarApiError(api, forms);
        setApiErrors(mapped);
        setSaveError(mapped.general ? SAVE_FAILED_TEXT : SAVE_FAILED_ROWS_TEXT);
      } else {
        setSaveError(SAVE_FAILED_TEXT);
      }
    } finally {
      setSaving(false);
    }
  };

  const blocked = attempted && liveErrors.hasErrors;

  useHeaderSave(
    { visible: dirty, mode: 'edit', saving, error: saveError },
    { onSave: handleSave, onCancel: handleCancel },
  );

  return (
    <div className="bz-page-settings flex flex-col min-h-0" data-testid="business-calendar-editor">
      <div className="p-4 md:p-6 flex flex-col gap-6 max-w-4xl">
        <div className="flex flex-col gap-2">
          <h2 className="m-0 text-base font-bold text-[var(--ink-900)]">Closures &amp; special days</h2>
          <p className="m-0 text-sm text-[var(--ink-500)]">
            These apply to every service of this business. A business closure wins over everything, then a
            service&apos;s own closures; a special day replaces every service&apos;s hours on that date; otherwise the normal hours apply.
          </p>
        </div>

        {upcoming && (() => {
          const { title, detail } = describeCalendarEvent(upcoming);
          return (
            <Notice variant="info" data-testid="calendar-next-up">
              <span className="font-semibold">Next in the app:</span> {title}{detail ? ` · ${detail}` : ''}
              {dirty ? ' (once saved)' : ''}
            </Notice>
          );
        })()}

        {errors.general && <Notice variant="error">{errors.general}</Notice>}
        {blocked && !errors.general && <Notice variant="error">Fix the highlighted rows before saving.</Notice>}

        {/* ---------- Closures ---------- */}
        <section aria-labelledby="bc-closures" className="flex flex-col gap-3">
          <h3 id="bc-closures" className="m-0 text-sm font-bold text-[var(--ink-900)] flex items-center gap-1.5">
            <CalendarOff size={14} /> Closures{forms.closures.length ? ` (${forms.closures.length})` : ''}
          </h3>
          <p className="m-0 text-xs text-[var(--ink-500)]">Whole days the business is closed — no bookings on any service. Dates are inclusive.</p>
          {forms.closures.length === 0 && (
            <p className="m-0 text-xs italic text-[var(--ink-400)]">No closures — the business follows its services&apos; hours every day.</p>
          )}
          {forms.closures.map((c, i) => {
            const rowErrors = errors.closures[i];
            const name = `Closure ${i + 1}`;
            return (
              <div key={c.id} data-testid={`closure-${i}`} className="rounded-lg p-3.5" style={cardStyle(!!rowErrors?.length)}>
                <div className="flex items-center justify-between mb-2.5">
                  <span className="text-[12px] font-semibold text-[var(--ink-700)]">{name}</span>
                  <Button type="button" variant="danger" style={{ padding: '4px 8px', fontSize: 11 }} aria-label={`Remove ${name}`}
                    onClick={() => edit((f) => ({ ...f, closures: f.closures.filter((_, j) => j !== i) }))}>
                    <X size={11} /> Remove
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {c.every_year ? (
                    <>
                      <MonthDayPicker label="From" name={`${name} start`} value={c.start_date} onChange={(v) => patchClosure(i, { start_date: v })} selectClassName={selectBox} />
                      <MonthDayPicker label="To (inclusive)" name={`${name} end`} value={c.end_date} onChange={(v) => patchClosure(i, { end_date: v })} selectClassName={selectBox} />
                    </>
                  ) : (
                    <>
                      <DateInput label="From" ariaLabel={`${name} start date`} value={c.start_date} invalid={!!rowErrors?.length} onChange={(v) => patchClosure(i, { start_date: v })} />
                      <DateInput label="To (inclusive)" ariaLabel={`${name} end date`} value={c.end_date} invalid={!!rowErrors?.length} onChange={(v) => patchClosure(i, { end_date: v })} />
                    </>
                  )}
                  <ReasonInputs prefix={name} en={c.reason_en} es={c.reason_es}
                    onChange={(lang, v) => patchClosure(i, lang === 'en' ? { reason_en: v } : { reason_es: v })} />
                </div>
                <EveryYearToggle label={`${name} every year`} checked={c.every_year === true}
                  onChange={(on) => patchClosure(i, (cur) => setClosureEveryYear(cur, on))}
                  hints={c.every_year ? recurringHints(c) : []} />
                <ErrorLines messages={rowErrors} />
              </div>
            );
          })}
          <Button type="button" onClick={addClosure} icon={<Plus size={13} />} style={{ alignSelf: 'flex-start' }}>Add closure</Button>
        </section>

        {/* ---------- Special days ---------- */}
        <section aria-labelledby="bc-special" className="flex flex-col gap-3">
          <h3 id="bc-special" className="m-0 text-sm font-bold text-[var(--ink-900)] flex items-center gap-1.5">
            <CalendarClock size={14} /> Special days{forms.specialDays.length ? ` (${forms.specialDays.length})` : ''}
          </h3>
          <p className="m-0 text-xs text-[var(--ink-500)]">
            A date with its own opening hours for every service (e.g. a shorter Christmas Eve). A day with no hours is a closure — add it above.
          </p>
          {forms.specialDays.length === 0 && (
            <p className="m-0 text-xs italic text-[var(--ink-400)]">No special days — every service keeps its normal hours.</p>
          )}
          {forms.specialDays.map((d, i) => {
            const rowErrors = errors.specialDays[i];
            const windowErrors = errors.windows[i] || {};
            const bad = !!rowErrors?.length || Object.keys(windowErrors).length > 0;
            const name = `Special day ${i + 1}`;
            return (
              <div key={d.id} data-testid={`special-day-${i}`} className="rounded-lg p-3.5" style={cardStyle(bad)}>
                <div className="flex items-center justify-between mb-2.5">
                  <span className="text-[12px] font-semibold text-[var(--ink-700)]">{name}</span>
                  <Button type="button" variant="danger" style={{ padding: '4px 8px', fontSize: 11 }} aria-label={`Remove ${name}`}
                    onClick={() => edit((f) => ({ ...f, specialDays: f.specialDays.filter((_, j) => j !== i) }))}>
                    <X size={11} /> Remove
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {d.every_year ? (
                    <MonthDayPicker label="Day" name={`${name} day`} value={d.date} onChange={(v) => patchSpecial(i, { date: v })} selectClassName={selectBox} />
                  ) : (
                    <DateInput label="Date" ariaLabel={`${name} date`} value={d.date} invalid={!!rowErrors?.length} onChange={(v) => patchSpecial(i, { date: v })} />
                  )}
                  <div className="hidden lg:block" />
                  <ReasonInputs prefix={name} en={d.reason_en} es={d.reason_es}
                    onChange={(lang, v) => patchSpecial(i, lang === 'en' ? { reason_en: v } : { reason_es: v })} />
                </div>

                <div className="mt-3 flex flex-col gap-2">
                  <span className="text-xs text-[var(--ink-500)]">Opening hours (a close at midnight, 00:00 / 12:00 AM, means end of day)</span>
                  {d.windows.map((w, wi) => (
                    <div key={wi} className="flex flex-col gap-1">
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <TimeField ariaLabel={`${name} window ${wi + 1} open`} value={w.open} invalid={!!windowErrors[wi]}
                          onChange={(v) => patchSpecial(i, (cur) => ({ ...cur, windows: cur.windows.map((x, k) => (k === wi ? { ...x, open: v } : x)) }))} />
                        <span className="text-[var(--ink-500)]">to</span>
                        <TimeField ariaLabel={`${name} window ${wi + 1} close`} value={closeToInput(w.close)} invalid={!!windowErrors[wi]}
                          onChange={(v) => patchSpecial(i, (cur) => ({ ...cur, windows: cur.windows.map((x, k) => (k === wi ? { ...x, close: closeFromInput(v) } : x)) }))} />
                        <button type="button" aria-label={`Remove ${name} window ${wi + 1}`} className="w-9 h-9 inline-flex items-center justify-center rounded-[8px] hover:opacity-70 bg-transparent border-0 cursor-pointer"
                          style={{ color: 'var(--ink-400)' }}
                          onClick={() => patchSpecial(i, (cur) => ({ ...cur, windows: cur.windows.filter((_, k) => k !== wi) }))}>
                          <X size={13} />
                        </button>
                      </div>
                      {windowErrors[wi] && <p role="alert" className="text-xs m-0" style={{ color: danger }}>{windowErrors[wi]}</p>}
                    </div>
                  ))}
                  <Button type="button" icon={<Plus size={12} />} style={{ alignSelf: 'flex-start', padding: '4px 8px', fontSize: 11 }}
                    onClick={() => patchSpecial(i, (cur) => ({ ...cur, windows: [...cur.windows, { open: '', close: '' }] }))}>
                    Add time window
                  </Button>
                </div>

                <EveryYearToggle label={`${name} every year`} checked={d.every_year === true}
                  onChange={(on) => patchSpecial(i, (cur) => setSpecialDayEveryYear(cur, on))}
                  hints={d.every_year && describeMonthDay(d.date) ? [`Every ${describeMonthDay(d.date)}`] : []} />
                {shadowed[i] && (
                  <p className="text-xs mt-2 mb-0 flex items-center gap-1.5" style={{ color: 'var(--warn-fg, #8a5a00)' }}>
                    <Info size={12} /> {shadowed[i]}
                  </p>
                )}
                <ErrorLines messages={rowErrors} />
              </div>
            );
          })}
          <Button type="button" onClick={addSpecial} icon={<Plus size={13} />} style={{ alignSelf: 'flex-start' }}>Add special day</Button>
        </section>
      </div>
    </div>
  );
};
